import React, { forwardRef, memo, useRef } from 'react';

import { useIsomorphicLayoutEffect } from '../../../../hooks/useIsomorphicLayoutEffect';
import { useMergeRefs } from '../../../../hooks/useMergeRefs';
import isNil from '../../../../utils/isNil';
import { getOptionIdentifier } from '../../Select.helpers';
import { SelectOption } from '../option/SelectOption';
import useSelectOption from '../option/useSelectOption';
import { useSelectRootContext } from '../root/SelectRootContext';

export type SelectOptionsProps = React.ComponentPropsWithoutRef<'div'> & {
    components?: {
        Option?: typeof SelectOption;
    };
};

export const Options = forwardRef<HTMLDivElement, SelectOptionsProps>(
    ({ components, ...rest }: SelectOptionsProps, ref) => {
        const wrapperRef = useRef<HTMLDivElement>(null);
        const mergedRefs = useMergeRefs([ref, wrapperRef]);
        const { Option = SelectOption } = components || {};

        const {
            optionKeyProp,
            searchContainerHeight,
            pointer,
            isVirtualScrollEnabled,
            isSearchFieldFocused,
            virtualScrollContainer,
            maxHeight,
            onPopupScroll,
            isFloatingDropdownPositioned,
            options: {
                optionsCount,
                activeIndex,
                rowVirtualizer,
                displayOptions,
                selectedIndex,
                optionsElementsRef
            }
        } = useSelectRootContext();

        const { getOptionProps } = useSelectOption();

        useIsomorphicLayoutEffect(() => {
            if (isFloatingDropdownPositioned) {
                // For virtual scrolling
                if (isVirtualScrollEnabled) {
                    if (isNil(activeIndex) && isNil(selectedIndex) && !pointer) {
                        rowVirtualizer.scrollToIndex(0);
                    }

                    // Scrolling is restored, but the item will be scrolled
                    // into view when necessary
                    if (!isNil(activeIndex)) {
                        // Only focus the list wrapper if the search input is not currently focused.
                        // This allows the user to type in the search field without losing focus.
                        if (!isSearchFieldFocused) {
                            wrapperRef.current?.focus({ preventScroll: true });
                        }
                        rowVirtualizer.scrollToIndex(activeIndex);
                    }
                }
                // For non-virtual scrolling
                else if (!isNil(activeIndex)) {
                    // The structure is: wrapperRef -> scroll container -> content container -> options
                    const scrollContainer = wrapperRef.current?.children[0] as HTMLElement; // The div with overflow: auto
                    const activeElement = wrapperRef.current?.querySelector(
                        `#item-${activeIndex}`
                    ) as HTMLElement;

                    if (activeElement && scrollContainer) {
                        // Calculate scroll position manually
                        const containerRect = scrollContainer.getBoundingClientRect();
                        const elementRect = activeElement.getBoundingClientRect();

                        // Check if element is already visible
                        const isVisible =
                            elementRect.top >= containerRect.top &&
                            elementRect.bottom <= containerRect.bottom;

                        if (!isVisible) {
                            setTimeout(() => {
                                // Scroll the container to bring the element into view
                                const scrollTop =
                                    activeElement.offsetTop - scrollContainer.clientHeight / 2;
                                scrollContainer.scrollTop = Math.max(0, scrollTop);
                            }, 100);
                        }
                    }
                }
            }
        }, [
            rowVirtualizer,
            isFloatingDropdownPositioned,
            activeIndex,
            selectedIndex,
            pointer,
            isSearchFieldFocused,
            isVirtualScrollEnabled
        ]);

        return (
            <div ref={mergedRefs} {...rest}>
                <div
                    ref={virtualScrollContainer}
                    style={{
                        maxHeight: `${maxHeight - searchContainerHeight}px`,
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                    onScroll={onPopupScroll}
                    tabIndex={-1}
                >
                    <div
                        style={{
                            height: isVirtualScrollEnabled
                                ? `${rowVirtualizer.getTotalSize()}px`
                                : 'auto',
                            width: '100%',
                            position: 'relative',
                            padding: '0 var(--spacing-2)'
                        }}
                        tabIndex={-1}
                    >
                        {isVirtualScrollEnabled
                            ? rowVirtualizer.getVirtualItems().map((virtualItem) => {
                                  const option = displayOptions[virtualItem.index];
                                  return (
                                      <Option
                                          key={virtualItem.key as string}
                                          virtual
                                          ref={(node: HTMLDivElement | null) => {
                                              optionsElementsRef.current[virtualItem.index] = node;
                                              rowVirtualizer.measureElement(node);
                                          }}
                                          aria-setsize={optionsCount}
                                          aria-posinset={virtualItem.index + 1}
                                          data-index={virtualItem.index}
                                          style={{
                                              position: 'absolute',
                                              top: 0,
                                              transform: `translateY(${virtualItem.start}px)`,
                                              width: '100%'
                                          }}
                                          {...getOptionProps(option, virtualItem.index)}
                                      />
                                  );
                              })
                            : displayOptions.map((option, index) => {
                                  return (
                                      <Option
                                          key={getOptionIdentifier(option, optionKeyProp)}
                                          ref={(node: HTMLDivElement | null) => {
                                              optionsElementsRef.current[index] = node;
                                          }}
                                          {...getOptionProps(option, index)}
                                      />
                                  );
                              })}
                    </div>
                </div>
            </div>
        );
    }
);

Options.displayName = 'SelectOptions';

export const SelectOptions = memo(Options);
